import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { GetOperationCatalogResponse } from '@/api/handlers/resources/operations/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { OperationsQueryKeys } from './operationsQueryKeys';

const STALE_TIME_MS = 5 * 60_000;

/**
 * The whole operation catalog.
 *
 * No `placeholderData`, unlike the trial balance's: nothing about this read
 * changes as the user works the screen — there is no date to move and no locale
 * to send — so there is never a previous answer to hold on screen while a
 * different one loads. The only refetch is the staleness one, and that one
 * resolves into the same rows.
 *
 * `enabled` is the caller's, because access is derived from the session
 * *before* fetching: a PIN/kiosk token is refused by every accounting endpoint
 * whatever role it carries, and a raw 403 tells the user nothing they can act
 * on. See `operationsAccess`.
 */
export const useOperationCatalogQuery = (enabled: boolean) => {
    const { apiClient } = useApi();

    return useQuery<ApiResponse<GetOperationCatalogResponse>>({
        queryKey: OperationsQueryKeys.catalog(),
        queryFn: () => apiClient.operations.getCatalog(),
        enabled,
        staleTime: STALE_TIME_MS,
    });
};
